import { z } from 'zod';

import { type BaseItem, BaseItemSchema, type SortOrder } from '../common.js';

export enum TriggerKind {
  HL7Message = 'HL7_MESSAGE',
  VoiceCall = 'VOICE_CALL',
  X12Message = 'X12_MESSAGE',
  SftpMessage = 'SFTP_MESSAGE',
  JobCompletion = 'JOB_COMPLETION',
  Form = 'FORM',
}

export enum FormEventType {
  Completion = 'Completion',
}

export const TRIGGER_KIND_LABELS: Record<TriggerKind, string> = {
  [TriggerKind.VoiceCall]: 'Voice Call',
  [TriggerKind.JobCompletion]: 'Job Completion',
  [TriggerKind.Form]: 'Form',
  [TriggerKind.HL7Message]: 'HL7 Message',
  [TriggerKind.X12Message]: 'X12 Message',
  [TriggerKind.SftpMessage]: 'SFTP Message',
};

/**
 * Trigger kinds that are currently enabled in the UI.
 * Keep only these trigger kinds until we have more certainty about event emission for others.
 * Other kinds (HL7Message, X12Message, SftpMessage) are hidden pending event emission verification.
 */
export const ENABLED_TRIGGER_KINDS = [
  TriggerKind.VoiceCall,
  TriggerKind.JobCompletion,
  TriggerKind.Form,
] as const;

export const ENABLED_TRIGGER_KIND_OPTIONS = ENABLED_TRIGGER_KINDS.map((kind) => ({
  label: TRIGGER_KIND_LABELS[kind],
  value: kind,
}));

export enum FlowTriggerResponsePayload {
  ParentTableOnly = 'ParentTableOnly',
  ParentTableAndAllChildTables = 'ParentTableAndAllChildTables',
}

export enum TerminalStatus {
  Success = 'Success',
  Failed = 'Failed',
}

export const BaseHl7MessageEventFlowTriggerSchema = z.object({
  kind: z.literal(TriggerKind.HL7Message),
  messageType: z.string().nullable(),
  messageTrigger: z.string().nullable(),
  sendingFacility: z.string().nullable(),
  flowId: z.string(),
  organizationId: z.string(),
});
export type BaseHl7MessageEventFlowTrigger = z.infer<typeof BaseHl7MessageEventFlowTriggerSchema>;

export interface Hl7MessageEventFlowTrigger
  extends Omit<SharedFlowTrigger, 'kind'>, BaseHl7MessageEventFlowTrigger {}

export const BaseX12MessageEventFlowTriggerSchema = z.object({
  kind: z.literal(TriggerKind.X12Message),
  interchangeSenderId: z.string().nullable(),
  transactionSetIdCode: z.string().nullable(),
  transactionSetPurposeCode: z.string().nullable(),
  flowId: z.string(),
  organizationId: z.string(),
});
export type BaseX12MessageEventFlowTrigger = z.infer<typeof BaseX12MessageEventFlowTriggerSchema>;

export interface X12MessageEventFlowTrigger
  extends Omit<SharedFlowTrigger, 'kind'>, BaseX12MessageEventFlowTrigger {}

export const BaseSftpMessageEventFlowTriggerSchema = z.object({
  kind: z.literal(TriggerKind.SftpMessage),
  fileType: z.string().nullable(),
  flowId: z.string(),
  organizationId: z.string(),
});
export type BaseSftpMessageEventFlowTrigger = z.infer<typeof BaseSftpMessageEventFlowTriggerSchema>;

export interface SftpMessageEventFlowTrigger
  extends Omit<SharedFlowTrigger, 'kind'>, BaseSftpMessageEventFlowTrigger {}

export const BaseJobCompletionEventFlowTriggerSchema = z.object({
  kind: z.literal(TriggerKind.JobCompletion),
  jobKind: z.string().nullable(),
  terminalStatus: z.nativeEnum(TerminalStatus).nullable(),
  flowId: z.string(),
  organizationId: z.string(),
  allowUnpublishedFlowRuns: z.boolean().optional(),
});
export type BaseJobCompletionEventFlowTrigger = z.infer<
  typeof BaseJobCompletionEventFlowTriggerSchema
>;

export interface JobCompletionEventFlowTrigger
  extends Omit<SharedFlowTrigger, 'kind'>, BaseJobCompletionEventFlowTrigger {}

export const BaseFormEventFlowTriggerSchema = z.object({
  kind: z.literal(TriggerKind.Form),
  type: z.nativeEnum(FormEventType),
  previsitFormTemplateId: z.string().nullable().optional(),
  flowId: z.string(),
  organizationId: z.string(),
  allowUnpublishedFlowRuns: z.boolean().optional(),
});
export type BaseFormEventFlowTrigger = z.infer<typeof BaseFormEventFlowTriggerSchema>;

export interface FormEventFlowTrigger
  extends Omit<SharedFlowTrigger, 'kind'>, BaseFormEventFlowTrigger {}

export const BaseVoiceEventFlowTriggerSchema = z.object({
  flowId: z.string(),
  kind: z.literal(TriggerKind.VoiceCall),
  organizationId: z.string(),
  phoneNumber: z.string(),
  assistantId: z.string().nullable(),
});
export type BaseVoiceEventFlowTrigger = z.infer<typeof BaseVoiceEventFlowTriggerSchema>;

export const BaseFlowTriggerSchema = z.discriminatedUnion('kind', [
  BaseHl7MessageEventFlowTriggerSchema,
  BaseX12MessageEventFlowTriggerSchema,
  BaseSftpMessageEventFlowTriggerSchema,
  BaseVoiceEventFlowTriggerSchema,
  BaseJobCompletionEventFlowTriggerSchema,
  BaseFormEventFlowTriggerSchema,
]);
export type BaseFlowTrigger = z.infer<typeof BaseFlowTriggerSchema>;

export const SharedFlowTriggerSchema = BaseItemSchema.extend({
  kind: z.nativeEnum(TriggerKind),
  flowId: z.string(),
  organizationId: z.string(),
  allowUnpublishedFlowRuns: z.boolean().optional(),
});
export type BaseSharedFlowTrigger = z.infer<typeof SharedFlowTriggerSchema>;

export const FlowTriggerSchema = z.discriminatedUnion('kind', [
  BaseHl7MessageEventFlowTriggerSchema.merge(SharedFlowTriggerSchema.omit({ kind: true })),
  BaseX12MessageEventFlowTriggerSchema.merge(SharedFlowTriggerSchema.omit({ kind: true })),
  BaseSftpMessageEventFlowTriggerSchema.merge(SharedFlowTriggerSchema.omit({ kind: true })),
  BaseVoiceEventFlowTriggerSchema.merge(SharedFlowTriggerSchema.omit({ kind: true })),
  BaseJobCompletionEventFlowTriggerSchema.merge(SharedFlowTriggerSchema.omit({ kind: true })),
  BaseFormEventFlowTriggerSchema.merge(SharedFlowTriggerSchema.omit({ kind: true })),
]);
export type FlowTrigger = z.infer<typeof FlowTriggerSchema>;

export interface VoiceEventFlowTrigger
  extends Omit<SharedFlowTrigger, 'kind'>, BaseVoiceEventFlowTrigger {}

export interface TriggerTypeMap {
  [TriggerKind.HL7Message]: Hl7MessageEventFlowTrigger;
  [TriggerKind.VoiceCall]: VoiceEventFlowTrigger;
  [TriggerKind.X12Message]: X12MessageEventFlowTrigger;
  [TriggerKind.SftpMessage]: SftpMessageEventFlowTrigger;
  [TriggerKind.JobCompletion]: JobCompletionEventFlowTrigger;
  [TriggerKind.Form]: FormEventFlowTrigger;
}

export const Paths: Record<TriggerKind, string> = {
  [TriggerKind.HL7Message]: 'hl7-message-events',
  [TriggerKind.VoiceCall]: 'voice-events',
  [TriggerKind.X12Message]: 'x12-message-events',
  [TriggerKind.SftpMessage]: 'sftp-message-events',
  [TriggerKind.JobCompletion]: 'job-completion-events',
  [TriggerKind.Form]: 'form-events',
};

export interface FindAllOptions {
  kind?: TriggerKind;
  flowId?: string;
  organizationId?: string | string[];
  payloadType?: FlowTriggerResponsePayload;
  limit?: number;
  offset?: number;
  orderBy?: string;
  orderDirection?: SortOrder;
}

export interface SharedFlowTrigger extends BaseSharedFlowTrigger, BaseItem {}
